import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X, ChevronRight, ChevronLeft, Check, Building2, Mail, Phone,
  Globe, MapPin, Zap, Award, Sparkles, Loader2, Calendar,
  Bot, LayoutDashboard, DollarSign, Rocket, CheckCircle, Users,
  FileText, ArrowRight, Lock, Eye, EyeOff,
  Crown, Trophy, AlertTriangle, TrendingUp, Radio, Megaphone, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const horseLogo = "https://customer-assets.emergentagent.com/job_b515077c-a195-4ba1-b1ea-7a33c6e19b28/artifacts/h2gv38bu_Untitled%20%281920%20x%201080%20px%29%20%281920%20x%20750%20px%29%20%281%29.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Get Started", subtitle: "Quick info to create your listing" },
  { id: 2, title: "Business Details", subtitle: "Tell us about your business" },
  { id: 3, title: "Create Password", subtitle: "Secure your dashboard access" },
  { id: 4, title: "Best of Best", subtitle: "Grow your business" },
];

// New hero image
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_b2594af0-79d2-45da-b0e9-6c60003f7938/artifacts/nm7bh8ez_ChatGPT%20Image%20Feb%206%2C%202026%2C%2003_01_02%20PM.png";
const MOBILE_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_2d16b1d2-be88-47b0-a62d-1489b615d343/artifacts/3g1qwqpe_Untitled%20%28320%20x%20100%20px%29.png";

// Booking link
const BOOKING_LINK = "https://link.oneforcelive.io/widget/booking/ERCPWxQwwCucE3vfgNO9";

export default function CreateListingModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedBusiness, setSubmittedBusiness] = useState(null);
  const [abandonedTracked, setAbandonedTracked] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1
    name: "",
    contact_email: "",
    contact_phone: "",
    sms_consent: false, // A2P consent checkbox
    // Step 2
    category_id: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    website: "",
    description: "",
    contact_name: "",
  });
  
  const [errors, setErrors] = useState({});
  const scrollRef = useRef(null);

  // Scroll modal content back to top whenever the step changes (important for
  // the final success step so the user sees the headline first).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [currentStep]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setCurrentStep(1);
      setFormData({
        name: "",
        contact_email: "",
        contact_phone: "",
        sms_consent: false,
        category_id: "",
        street_address: "",
        city: "",
        state: "",
        zip_code: "",
        website: "",
        description: "",
        contact_name: "",
      });
      setErrors({});
      setSubmittedBusiness(null);
      setAbandonedTracked(false);
      setPassword("");
      setConfirmPassword("");
      setAuthToken(null);
    }
  }, [isOpen]);

  // Track abandoned checkout when user leaves step 2 without completing
  useEffect(() => {
    if (currentStep === 2 && formData.name && formData.contact_email && !abandonedTracked) {
      // Set up abandoned tracking - will fire if they close without submitting
      const handleBeforeUnload = () => {
        trackAbandonedCheckout();
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [currentStep, formData, abandonedTracked]);

  const trackAbandonedCheckout = async () => {
    if (abandonedTracked || !formData.name || !formData.contact_email) return;
    
    try {
      await axios.post(`${API}/track-abandoned-listing`, {
        business_name: formData.name,
        email: formData.contact_email,
        phone: formData.contact_phone,
        step_abandoned: currentStep,
      });
      setAbandonedTracked(true);
    } catch (error) {
      console.error("Failed to track abandoned checkout:", error);
    }
  };

  const handleClose = () => {
    // Track abandonment if on step 2 and has step 1 data
    if (currentStep === 2 && formData.name && formData.contact_email && !abandonedTracked) {
      trackAbandonedCheckout();
    }
    onClose();
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = "Business name is required";
    }
    if (!formData.contact_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "Valid email is required";
    }
    if (!formData.contact_phone || formData.contact_phone.length < 10) {
      newErrors.contact_phone = "Valid phone number is required";
    }
    if (!formData.sms_consent) {
      newErrors.sms_consent = "You must consent to receive communications to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.category_id || formData.category_id.length < 2) {
      newErrors.category_id = "Please enter your business category";
    }
    if (!formData.street_address || formData.street_address.length < 3) {
      newErrors.street_address = "Street address is required";
    }
    if (!formData.city || formData.city.length < 2) {
      newErrors.city = "City is required";
    }
    if (!formData.state || formData.state.length < 2) {
      newErrors.state = "State is required";
    }
    if (!formData.zip_code || formData.zip_code.length < 5) {
      newErrors.zip_code = "Valid ZIP code is required";
    }
    if (!formData.description || formData.description.length < 50) {
      newErrors.description = "Description must be at least 50 characters";
    }
    if (!formData.contact_name || formData.contact_name.length < 2) {
      newErrors.contact_name = "Contact name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        await submitListing();
      }
    } else if (currentStep === 3) {
      // Create account with password
      if (!password || password.length < 8) {
        setErrors({ password: "Password must be at least 8 characters" });
        return;
      }
      if (password !== confirmPassword) {
        setErrors({ confirmPassword: "Passwords do not match" });
        return;
      }
      setErrors({});
      await registerAccount();
    } else if (currentStep === 4) {
      // "No thanks" — go to dashboard
      goToDashboard();
    }
  };

  const registerAccount = async () => {
    setIsRegistering(true);
    try {
      // Parse contact_name into first/last
      const nameParts = (formData.contact_name || "User").trim().split(" ");
      const firstName = nameParts[0] || "User";
      const lastName = nameParts.slice(1).join(" ") || "Account";

      const res = await axios.post(`${API}/v1/auth/register`, {
        first_name: firstName,
        last_name: lastName,
        email: formData.contact_email,
        password: password,
        business_name: formData.name,
      });

      setAuthToken(res.data.token);
      // Store token so they're logged in
      localStorage.setItem("auth_token", res.data.token);
      localStorage.setItem("auth_user", JSON.stringify(res.data.user));
      toast.success("Account created! You can now manage your listing.");
      setCurrentStep(4);
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (detail.includes("already registered") || detail.includes("already exists")) {
        // Account exists — try logging in
        try {
          const loginRes = await axios.post(`${API}/v1/auth/login`, {
            email: formData.contact_email,
            password: password,
          });
          setAuthToken(loginRes.data.token);
          localStorage.setItem("auth_token", loginRes.data.token);
          localStorage.setItem("auth_user", JSON.stringify(loginRes.data.user));
          toast.success("Logged in successfully!");
          setCurrentStep(4);
        } catch (loginErr) {
          toast.error("This email already has an account. Please use Dashboard Login instead.");
        }
      } else {
        toast.error(detail || "Failed to create account. Please try again.");
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleBestOfBestInterest = async () => {
    // Save interest as a lead + tag "contact me about best of best"
    try {
      await axios.post(`${API}/best-of-best-interest`, {
        email: formData.contact_email,
        business_name: formData.name,
        phone: formData.contact_phone,
        interested: true,
      });
    } catch (e) {
      // Silent — don't block the flow
    }
    // Open the apply-for-#1 funnel in a new tab; leave the modal open so the
    // user can still go back to the directory or login to their dashboard.
    window.open("https://aihpower.com/start", "_blank", "noopener,noreferrer");
  };

  const handleNotInterested = async () => {
    // Tag "not interested in best of best"
    try {
      await axios.post(`${API}/best-of-best-interest`, {
        email: formData.contact_email,
        business_name: formData.name,
        phone: formData.contact_phone,
        interested: false,
      });
    } catch (e) {
      // Silent
    }
    goToDashboard();
  };

  const goToDashboard = () => {
    onClose();
    navigate("/dashboard");
  };

  const handleBack = () => {
    if (currentStep > 1 && currentStep <= 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitListing = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        services: [],
        products: [],
      };

      const res = await axios.post(`${API}/submit-business`, payload);
      
      toast.success("Your listing is live! 🎉", {
        description: "Your business has been added to the directory.",
      });
      
      setSubmittedBusiness(res.data);
      setAbandonedTracked(true); // Prevent abandoned tracking after successful submit
      setCurrentStep(3);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to create listing", {
        description: error.response?.data?.detail || "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden bg-gradient-to-b from-[#0F172A] to-[#020817] rounded-2xl border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            aria-label="Close"
            data-testid="create-listing-close-btn"
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Progress Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
            <motion.div
              className="h-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF]"
              initial={{ width: "0%" }}
              animate={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-center gap-2 pt-6 pb-2">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs transition-all ${
                  currentStep === step.id
                    ? "bg-[#7C3AED]/30 text-[#7C3AED] border border-[#7C3AED]/50"
                    : currentStep > step.id
                    ? "bg-[#10B981]/20 text-[#10B981]"
                    : "bg-white/5 text-[#64748B]"
                }`}
              >
                {currentStep > step.id ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <span>{step.id}</span>
                )}
                <span className="hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>

          <div ref={scrollRef} className="overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Step 1 & 2: Form Steps */}
            {currentStep <= 2 && (
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Left: Hero Image - Desktop only */}
                <div className="relative bg-[#0F172A] lg:min-h-[500px] hidden lg:block">
                  <img
                    src={HERO_IMAGE}
                    alt="LocalBest.ai Business Listing"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay Text on Image */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Join the LocalBest.ai Directory
                    </h3>
                    <p className="text-[#94A3B8]">
                      Get your own AI Business Hero today
                    </p>
                  </div>
                </div>

                {/* Right: Form */}
                <div className="p-6 lg:p-8">
                  <AnimatePresence mode="wait">
                    {currentStep === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-6"
                      >
                        {/* Mobile/Tablet Image - Different from desktop */}
                        <div className="lg:hidden mb-4 rounded-xl overflow-hidden bg-[#0F172A]">
                          <img
                            src={MOBILE_HERO_IMAGE}
                            alt="LocalBest.ai Business Listing"
                            className="w-full h-auto object-contain"
                          />
                        </div>
                        
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-4">
                            <Zap className="w-4 h-4 text-[#10B981]" />
                            <span className="text-sm text-[#10B981]">Step 1 of 4</span>
                          </div>
                          <h2 className="text-2xl font-bold text-white mb-2">
                            Let's Get Started! 🚀
                          </h2>
                          <p className="text-[#94A3B8]">
                            Just 3 quick fields to create your free listing
                          </p>
                        </div>

                        {/* Business Name */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Business Name *
                          </label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                            <Input
                              value={formData.name}
                              onChange={(e) => updateField("name", e.target.value)}
                              placeholder="Your Business Name"
                              className={`pl-11 py-6 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-lg ${
                                errors.name ? "border-red-500" : ""
                              }`}
                            />
                          </div>
                          {errors.name && (
                            <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Email Address *
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                            <Input
                              type="email"
                              value={formData.contact_email}
                              onChange={(e) => updateField("contact_email", e.target.value)}
                              placeholder="you@business.com"
                              className={`pl-11 py-6 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-lg ${
                                errors.contact_email ? "border-red-500" : ""
                              }`}
                            />
                          </div>
                          {errors.contact_email && (
                            <p className="text-red-400 text-sm mt-1">{errors.contact_email}</p>
                          )}
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Phone Number *
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                            <Input
                              type="tel"
                              value={formData.contact_phone}
                              onChange={(e) => updateField("contact_phone", e.target.value)}
                              placeholder="+1 (555) 123-4567"
                              className={`pl-11 py-6 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-lg ${
                                errors.contact_phone ? "border-red-500" : ""
                              }`}
                            />
                          </div>
                          {errors.contact_phone && (
                            <p className="text-red-400 text-sm mt-1">{errors.contact_phone}</p>
                          )}
                        </div>

                        {/* A2P SMS/Call Consent Checkbox */}
                        <div className={`p-4 rounded-xl border ${errors.sms_consent ? 'border-red-500 bg-red-500/5' : 'border-white/10 bg-white/5'}`}>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <div className="relative flex-shrink-0 mt-0.5">
                              <input
                                type="checkbox"
                                checked={formData.sms_consent}
                                onChange={(e) => updateField("sms_consent", e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                formData.sms_consent 
                                  ? 'bg-[#7C3AED] border-[#7C3AED]' 
                                  : 'border-white/30 bg-transparent'
                              }`}>
                                {formData.sms_consent && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                            <span className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
                              I consent to receive calls and text messages (including automated messages) from LocalBest.ai and its partners regarding my business listing, platform access, and related services. Message frequency may vary. Reply STOP to opt out. Message & data rates may apply.
                            </span>
                          </label>
                          {errors.sms_consent && (
                            <p className="text-red-400 text-xs mt-2 ml-8">{errors.sms_consent}</p>
                          )}
                        </div>

                        {/* Benefits */}
                        <div className="grid grid-cols-3 gap-3 pt-2">
                          {[
                            { icon: Award, label: "SEO Listing" },
                            { icon: Bot, label: "AI Hero" },
                            { icon: LayoutDashboard, label: "Dashboard" },
                          ].map((item, idx) => (
                            <div key={idx} className="text-center p-3 rounded-lg bg-white/5">
                              <item.icon className="w-5 h-5 text-[#7C3AED] mx-auto mb-1" />
                              <span className="text-xs text-[#94A3B8]">{item.label}</span>
                            </div>
                          ))}
                        </div>

                        {/* Continue Button */}
                        <Button
                          onClick={handleNext}
                          className="w-full py-6 text-lg bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90"
                        >
                          Continue <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>

                        <p className="text-center text-[#64748B] text-sm">
                          No credit card required • Takes 60 seconds
                        </p>
                      </motion.div>
                    )}

                    {currentStep === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-5"
                      >
                        <div>
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 mb-4">
                            <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                            <span className="text-sm text-[#7C3AED]">Step 2 of 4</span>
                          </div>
                          <h2 className="text-2xl font-bold text-white mb-2">
                            Almost Done! ✨
                          </h2>
                          <p className="text-[#94A3B8]">
                            A few more details to complete your listing
                          </p>
                        </div>

                        {/* Contact Name */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Your Name *
                          </label>
                          <Input
                            value={formData.contact_name}
                            onChange={(e) => updateField("contact_name", e.target.value)}
                            placeholder="John Doe"
                            className={`py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                              errors.contact_name ? "border-red-500" : ""
                            }`}
                          />
                          {errors.contact_name && (
                            <p className="text-red-400 text-sm mt-1">{errors.contact_name}</p>
                          )}
                        </div>

                        {/* Category - Text Input */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Business Category *
                          </label>
                          <Input
                            value={formData.category_id}
                            onChange={(e) => updateField("category_id", e.target.value)}
                            placeholder="e.g. Restaurant, Plumber, Real Estate, etc."
                            className={`py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                              errors.category_id ? "border-red-500" : ""
                            }`}
                          />
                          {errors.category_id && (
                            <p className="text-red-400 text-sm mt-1">{errors.category_id}</p>
                          )}
                        </div>

                        {/* Address Fields - Separated for CRM */}
                        <div className="space-y-3">
                          <label className="block text-white text-sm font-medium">
                            Business Address *
                          </label>
                          
                          {/* Street Address */}
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                            <Input
                              value={formData.street_address}
                              onChange={(e) => updateField("street_address", e.target.value)}
                              placeholder="Street Address (e.g. 123 Main St)"
                              className={`pl-11 py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                                errors.street_address ? "border-red-500" : ""
                              }`}
                            />
                          </div>
                          {errors.street_address && (
                            <p className="text-red-400 text-sm mt-1">{errors.street_address}</p>
                          )}
                          
                          {/* City, State, ZIP in a row */}
                          <div className="grid grid-cols-6 gap-2">
                            {/* City - takes more space */}
                            <div className="col-span-3">
                              <Input
                                value={formData.city}
                                onChange={(e) => updateField("city", e.target.value)}
                                placeholder="City"
                                className={`py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                                  errors.city ? "border-red-500" : ""
                                }`}
                              />
                              {errors.city && (
                                <p className="text-red-400 text-xs mt-1">{errors.city}</p>
                              )}
                            </div>
                            
                            {/* State */}
                            <div className="col-span-1">
                              <Input
                                value={formData.state}
                                onChange={(e) => updateField("state", e.target.value.toUpperCase())}
                                placeholder="State"
                                maxLength={2}
                                className={`py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-center ${
                                  errors.state ? "border-red-500" : ""
                                }`}
                              />
                              {errors.state && (
                                <p className="text-red-400 text-xs mt-1">{errors.state}</p>
                              )}
                            </div>
                            
                            {/* ZIP Code */}
                            <div className="col-span-2">
                              <Input
                                value={formData.zip_code}
                                onChange={(e) => updateField("zip_code", e.target.value.replace(/\D/g, '').slice(0, 5))}
                                placeholder="ZIP Code"
                                maxLength={5}
                                className={`py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                                  errors.zip_code ? "border-red-500" : ""
                                }`}
                              />
                              {errors.zip_code && (
                                <p className="text-red-400 text-xs mt-1">{errors.zip_code}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Website — Instant Brand Match */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2 flex items-center gap-2 flex-wrap">
                            Website
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white">
                              Instantly Brands Your Listing
                            </span>
                          </label>
                          <div className="relative">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                            <Input
                              value={formData.website}
                              onChange={(e) => updateField("website", e.target.value)}
                              placeholder="https://yourbusiness.com"
                              className="pl-11 py-5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                            />
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed">
                            Add your website and LocalBest.ai automatically pulls in your logo, brand colors, images, and business details — so your Best of Best Listing looks branded and on-point from day one.
                          </p>
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            Business Description *
                          </label>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => updateField("description", e.target.value)}
                            placeholder="Tell us about your business, services, and what makes you the best..."
                            className={`min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-[#64748B] ${
                              errors.description ? "border-red-500" : ""
                            }`}
                          />
                          {errors.description && (
                            <p className="text-red-400 text-sm mt-1">{errors.description}</p>
                          )}
                          <p className="text-[#64748B] text-xs mt-1">
                            {formData.description.length}/50 characters minimum
                          </p>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 pt-2">
                          <Button
                            variant="outline"
                            onClick={handleBack}
                            className="flex-1 py-5 border-white/20 text-white hover:bg-white/10"
                          >
                            <ChevronLeft className="w-4 h-4 mr-2" /> Back
                          </Button>
                          <Button
                            onClick={handleNext}
                            disabled={isSubmitting}
                            className="flex-[2] py-5 bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                Create My Free Listing <CheckCircle className="w-5 h-5 ml-2" />
                              </>
                            )}
                          </Button>
                        </div>

                        <p className="text-center text-[#64748B] text-sm">
                          No credit card required. Upgrade anytime.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Step 3: Listing Created + Create Password */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                </motion.div>

                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Your Listing Is Live!
                  </h2>
                  <p className="text-[#94A3B8] max-w-md mx-auto">
                    Now create a password to access your dashboard and manage your listing.
                  </p>
                </div>

                {/* Password Form */}
                <div className="max-w-md mx-auto space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Email</label>
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg">
                      <Mail className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[#94A3B8] text-sm">{formData.contact_email}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Create Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: null })); }}
                        placeholder="Min. 8 characters"
                        className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                        data-testid="create-password"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Confirm Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setErrors(p => ({ ...p, confirmPassword: null })); }}
                        placeholder="Re-enter your password"
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                        data-testid="confirm-password"
                      />
                    </div>
                    {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                  <Button
                    onClick={handleNext}
                    disabled={isRegistering}
                    className="w-full py-5 text-lg bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90"
                    data-testid="create-account-btn"
                  >
                    {isRegistering ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating Account...</>
                    ) : (
                      <><CheckCircle className="w-5 h-5 mr-2" /> Create My Account</>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Best of Best Offer — bold upgrade moment */}
            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="p-6 md:p-8"
                data-testid="step-4-best-of-best"
              >
                {/* SUCCESS RIBBON */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#10B981]/15 border border-[#10B981]/30">
                    <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" />
                    <span className="text-xs font-semibold text-[#10B981]">Your free listing is live</span>
                  </span>
                </div>

                {/* HEADLINE */}
                <div className="text-center mb-7">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-[1.1] font-['Outfit']">
                    Your Free Listing Is Live.
                    <br />
                    <span className="bg-gradient-to-r from-[#FBBF24] via-[#F59E0B] to-[#EF4444] bg-clip-text text-transparent">
                      But the #1 Spot in Your Market Is Still Up for Grabs.
                    </span>
                  </h2>
                  <p className="mt-4 text-sm sm:text-base text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
                    A free listing gets you in the LocalBest.ai marketplace. But only <span className="text-white font-semibold">one approved business per category and market</span> can become the Best of Best featured provider — earning priority placement and the top dispatch position for local leads.
                  </p>
                </div>

                {/* BOLD CALLOUT — FOMO #1 */}
                <div className="rounded-2xl border border-[#F59E0B]/40 bg-gradient-to-br from-[#F59E0B]/10 via-[#0F172A] to-[#EF4444]/10 p-5 mb-6">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                    <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                      Do Not Let a Competitor Lock In Your Category First
                    </h3>
                  </div>
                  <p className="text-sm text-[#CBD5E1] leading-relaxed mb-4">
                    LocalBest.ai helps customers find trusted local businesses fast. When someone in your area is searching, clicking, calling, or claiming offers, the Best of Best provider is positioned for the highest visibility, trust, and priority promotion.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
                      <p className="text-[#94A3B8]">Free listing gives you</p>
                      <p className="text-white font-bold mt-1">Presence</p>
                    </div>
                    <div className="rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3">
                      <p className="text-[#F59E0B]">Best of Best gives you</p>
                      <p className="text-white font-bold mt-1">Position</p>
                    </div>
                    <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
                      <p className="text-[#94A3B8]">AI Horse-Powered gives you</p>
                      <p className="text-white font-bold mt-1">Promotion</p>
                    </div>
                  </div>
                </div>

                {/* WHAT YOU COULD UNLOCK */}
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] text-center mb-3">What You Could Unlock</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {[
                      { icon: Crown,         label: "#1 Best of Best Position" },
                      { icon: Trophy,        label: "Priority LocalBest.ai Placement" },
                      { icon: Target,        label: "Top Dispatch Consideration" },
                      { icon: DollarSign,    label: "$120,000 Co-Op Marketing Eligibility" },
                      { icon: Bot,           label: "AI Horse-Powered CRM & AI Agents" },
                      { icon: Sparkles,      label: "Cinematic Ads & Retargeting" },
                      { icon: Globe,         label: "Facebook, Instagram, Google, Radio & CTV" },
                      { icon: FileText,      label: "Unlimited Orders, Tasks & Marketing Support" },
                      { icon: TrendingUp,    label: "Lead Capture, Follow-Up & Automation" },
                    ].map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#F59E0B]/40 transition min-w-0"
                      >
                        <item.icon className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                        <span className="text-xs text-[#E2E8F0] truncate">{item.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* FOMO #2 — strong */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-5">
                  <p className="text-white font-bold text-base mb-2">
                    If you stay free, you still get listed.
                  </p>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">
                    But if another business in your category gets approved first, they may become the featured Best of Best provider in your market — promoted first, displayed first, recommended first, and positioned as the trusted local choice while everyone else stays listed below them.
                  </p>
                </div>

                {/* Tagline */}
                <p className="text-center text-sm sm:text-base text-[#FBBF24] italic mb-6 px-2">
                  The free listing puts you on the map. <span className="text-white font-semibold not-italic">Best of Best helps you own the map.</span>
                </p>

                {/* CTAs */}
                <div className="flex flex-col gap-3 max-w-xl mx-auto">
                  <Button
                    onClick={handleBestOfBestInterest}
                    className="w-full py-6 text-base sm:text-lg font-bold bg-gradient-to-r from-[#FBBF24] via-[#F59E0B] to-[#EF4444] hover:opacity-95 shadow-xl shadow-[#F59E0B]/20"
                    data-testid="best-of-best-yes"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    Apply for the #1 Best of Best Spot
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    onClick={handleNotInterested}
                    variant="outline"
                    className="w-full py-5 text-sm border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white"
                    data-testid="best-of-best-no"
                  >
                    No Thanks, Keep Me on the Free Listing Only
                  </Button>
                </div>

                <p className="text-center text-[11px] text-[#64748B] mt-5">
                  Plus promotion on Radio, Connected TV, Postcards, Billboards & more.{" "}
                  <a href="https://localbest.ai/cinematic-stampede" target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:underline">
                    Learn more
                  </a>
                </p>
              </motion.div>
            )}

            {/* Book a Call Footer - Shows on steps 3+ */}
            {currentStep >= 3 && (
              <div className="border-t border-white/10 p-4 text-center bg-white/5">
                <p className="text-[#64748B] text-sm mb-2">
                  Have questions or need help?
                </p>
                <a
                  href={BOOKING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#00E5FF] hover:text-[#7C3AED] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book a Call with Our Team
                </a>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
